import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import Button from '@/components/Button/Button';

export type SubTaskItemLayout = 'default' | 'tasklistAccordion';

export interface SubTaskItemProps {
  itemId: string;
  anchorId?: string;
  label: string;
  completed: boolean;
  onToggleComplete: () => void;
  disabled?: boolean;
  children?: React.ReactNode;
  /** Secondary checklist pages: chevron + title row, “I Did This” on the right. */
  layout?: SubTaskItemLayout;
  /** Teacher preview: expand/collapse only — no completion controls. */
  readOnly?: boolean;
}

const ChevronDownIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M5 7.5L10 12.5L15 7.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronUpIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="24"
    height="24"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M5 12.5L10 7.5L15 12.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronIcon: React.FC<{ className?: string }> = ({ className }) => (
  <ChevronDownIcon className={className} />
);

const DidThisCheckIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="14"
    height="11"
    viewBox="0 0 14 11"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M12.25 1.625L5.25 8.625L1.75 5.125"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function splitTitleTail(title: string): { head: string; tail: string } {
  const trimmed = title.trim();
  const lastSpace = trimmed.lastIndexOf(' ');
  if (lastSpace < 0) {
    return { head: '', tail: trimmed };
  }
  return {
    head: trimmed.slice(0, lastSpace),
    tail: trimmed.slice(lastSpace + 1),
  };
}

const SubTaskItem: React.FC<SubTaskItemProps> = ({
  itemId,
  anchorId,
  label,
  completed,
  onToggleComplete,
  disabled = false,
  children,
  layout = 'default',
  readOnly = false,
}) => {
  const reactId = useId();
  const panelId = `subtask-panel-${itemId}-${reactId.replace(/:/g, '')}`;
  const triggerId = `${panelId}-trigger`;
  const hasDetails = Boolean(children);
  const isAccordion = layout === 'tasklistAccordion';
  /** Checklist secondary: expanded while incomplete; collapse when marking done; complete rows can expand to read details and unmark. */
  const [open, setOpen] = useState(() => (isAccordion ? !completed : false));
  const prevCompletedRef = useRef<boolean | null>(null);

  useEffect(() => {
    if (!isAccordion) return;
    const prev = prevCompletedRef.current;
    if (completed) {
      if (prev === false) {
        setOpen(false);
      }
      prevCompletedRef.current = completed;
      return;
    }
    if (prev === null) {
      prevCompletedRef.current = completed;
      return;
    }
    if (prev !== completed) {
      prevCompletedRef.current = completed;
      setOpen(true);
    }
  }, [isAccordion, completed]);

  const stopProp = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation();
  }, []);

  if (isAccordion) {
    const showUnmarkLink = Boolean(completed && (open || !hasDetails));
    const titleText = label.trim() || 'Step';
    const { head, tail } = splitTitleTail(titleText);
    const ChevronLead = open ? ChevronUpIcon : ChevronDownIcon;
    const chevronClass = `sub-task-item__chevron-lead-icon${completed ? ' sub-task-item__chevron-lead-icon--muted' : ''}`;

    const toggleExpanded = () => {
      if (!hasDetails) return;
      setOpen((v) => !v);
    };

    const onHeaderKeyDown = (e: React.KeyboardEvent) => {
      if (!hasDetails) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleExpanded();
      }
    };

    const didThisIcon = (
      <DidThisCheckIcon className="sub-task-item__did-this-inline-icon" aria-hidden="true" />
    );
    const didThisIconFilled = (
      <DidThisCheckIcon className="sub-task-item__did-this-inline-icon sub-task-item__did-this-inline-icon--on-fill" aria-hidden="true" />
    );

    return (
      <li
        id={anchorId}
        className={`sub-task-item sub-task-item--tasklist-accordion${!hasDetails ? ' sub-task-item--accordion-no-details' : ''}${completed ? ' sub-task-item--accordion-done' : ''}${open && hasDetails ? ' sub-task-item--accordion-open' : ''}${anchorId ? ' tasklist-json-anchor' : ''}`}
      >
        <div
          className={`sub-task-item__accordion-sticky${open && hasDetails ? ' sub-task-item__accordion-sticky--open' : ''}${completed ? ' sub-task-item__accordion-sticky--done' : ''}`}
        >
          <div className="sub-task-item__accordion-header-row">
            {hasDetails ? (
              <div
                role="button"
                tabIndex={0}
                id={triggerId}
                className="sub-task-item__header-toggle"
                aria-expanded={open}
                aria-controls={panelId}
                onClick={toggleExpanded}
                onKeyDown={onHeaderKeyDown}
              >
                <span className="sub-task-item__chevron-lead" aria-hidden="true">
                  <ChevronLead className={chevronClass} />
                </span>
                <h3 className={`sub-task-item__accordion-heading${completed ? ' sub-task-item__accordion-heading--done' : ''}`}>
                  {head ? (
                    <>
                      {head}{' '}
                    </>
                  ) : null}
                  <span className="sub-task-item__accordion-heading-tail">
                    {tail}
                    {completed && !readOnly ? (
                      <>
                        {'\u00A0'}
                        <span className="sub-task-item__title-check-pill" aria-label="Completed">
                          <DidThisCheckIcon className="sub-task-item__title-check-pill-icon" aria-hidden="true" />
                        </span>
                      </>
                    ) : null}
                  </span>
                </h3>
              </div>
            ) : (
              <div className="sub-task-item__header-toggle sub-task-item__header-toggle--static">
                <h3 className={`sub-task-item__accordion-heading${completed ? ' sub-task-item__accordion-heading--done' : ''}`}>
                  {head ? (
                    <>
                      {head}{' '}
                    </>
                  ) : null}
                  <span className="sub-task-item__accordion-heading-tail">
                    {tail}
                    {completed && !readOnly ? (
                      <>
                        {'\u00A0'}
                        <span className="sub-task-item__title-check-pill" aria-label="Completed">
                          <DidThisCheckIcon className="sub-task-item__title-check-pill-icon" aria-hidden="true" />
                        </span>
                      </>
                    ) : null}
                  </span>
                </h3>
              </div>
            )}

            {!readOnly && !completed && !open ? (
              <span className="sub-task-item__did-this-slot">
                <Button
                  type="button"
                  size="sm"
                  variant="border"
                  color="primary"
                  icon={didThisIcon}
                  iconPosition="before"
                  label="I Did This"
                  onClick={() => onToggleComplete()}
                  disabled={disabled}
                  className="sub-task-item__did-this-btn"
                />
              </span>
            ) : null}

            {!readOnly && !completed && open ? (
              <span className="sub-task-item__did-this-slot">
                <Button
                  type="button"
                  size="sm"
                  variant="fill"
                  color="primary"
                  icon={didThisIconFilled}
                  iconPosition="before"
                  label="I Did This"
                  onClick={() => onToggleComplete()}
                  disabled={disabled}
                  className="sub-task-item__did-this-btn sub-task-item__did-this-btn--fill"
                />
              </span>
            ) : null}

            {!readOnly && showUnmarkLink ? (
              <span className="sub-task-item__did-this-slot">
                <button
                  type="button"
                  className="sub-task-item__unmark-link"
                  disabled={disabled}
                  aria-label={`Unmark "${titleText}" as done`}
                  onMouseDown={stopProp}
                  onClick={(e) => {
                    stopProp(e);
                    onToggleComplete();
                  }}
                >
                  Unmark as Done
                </button>
              </span>
            ) : null}
          </div>
        </div>

        {hasDetails && (
          <div
            id={panelId}
            role="region"
            aria-labelledby={triggerId}
            hidden={!open}
            className="sub-task-item__panel sub-task-item__panel--accordion"
          >
            <div className="sub-task-item__body sub-task-item__body--accordion">{children}</div>
          </div>
        )}
      </li>
    );
  }

  return (
    <li
      id={anchorId}
      className={`sub-task-item${anchorId ? ' tasklist-json-anchor' : ''}`}
    >
      <div className="sub-task-item__row">
        {!readOnly ? (
        <button
          type="button"
          className={`sub-task-item__complete${completed ? ' sub-task-item__complete--done' : ''}`}
          aria-pressed={completed}
          disabled={disabled}
          aria-label={completed ? `${label}, completed. Mark as not done` : `${label}, not completed. Mark as done`}
          onClick={(e) => {
            e.stopPropagation();
            onToggleComplete();
          }}
        >
          {completed ? (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path
                d="M13.25 4.75L6.25 11.75L2.75 8.25"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg
              className="sub-task-item__complete-placeholder"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M13.25 4.75L6.25 11.75L2.75 8.25"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.35"
              />
            </svg>
          )}
        </button>
        ) : null}

        {hasDetails ? (
          <button
            type="button"
            className="sub-task-item__toggle"
            aria-expanded={open}
            aria-controls={panelId}
            id={`${panelId}-trigger`}
            onClick={() => setOpen((v) => !v)}
          >
            <span className="sub-task-item__label">{label}</span>
            <span className={`sub-task-item__chevron-wrap${open ? ' is-open' : ''}`}>
              <ChevronIcon className="sub-task-item__chevron" />
            </span>
          </button>
        ) : (
          <span className="sub-task-item__label-static">{label}</span>
        )}
      </div>

      {hasDetails && (
        <div
          id={panelId}
          role="region"
          aria-labelledby={`${panelId}-trigger`}
          hidden={!open}
          className="sub-task-item__panel"
        >
          <div className="sub-task-item__body">{children}</div>
        </div>
      )}
    </li>
  );
};

export default SubTaskItem;
